import { ukPlural } from "./plural.ts";

const n = (value: number) => value.toLocaleString("uk-UA");

/** Ukrainian, the default language. `en.ts` must have the same keys (checked by the `Dict` type). */
export const uk = {
  meta: {
    locale: "uk-UA",
    htmlLang: "uk",
  },
  brand: {
    name: "Графа 33",
    short: "Графа 33",
    tagline: "Код УКТЗЕД для будь-якого товару — з джерелами, рішеннями та BTI",
    values: [
      {
        title: "Досліджує товар у мережі",
        text: "Сторінки виробника, каталоги й техлисти — з посиланнями на кожне джерело.",
      },
      {
        title: "Проходить українським тарифом",
        text: "Від розділу до 10-значного коду разом із примітками до розділів і груп.",
      },
      {
        title: "Перевіряє рішення та BTI",
        text: "Рішення Держмитслужби щодо класифікації та європейські BTI для обраного коду.",
      },
    ],
    formCaption: "Митна декларація · графа 33 — код товару",
    form: {
      box31: "Вантажні місця та опис товарів",
      box31Value: "Підшипник кульковий радіальний SKF 6205-2RSH, 20 шт.",
      box32: "Товар №",
      box33: "Код товару",
      box34: "Код країни походження",
      box35: "Вага брутто (кг)",
      box38: "Вага нетто (кг)",
    },
  },
  units: {
    hour: "год",
    minute: "хв",
    second: "с",
    millisecond: "мс",
    byte: "Б",
    kilobyte: "КБ",
    megabyte: "МБ",
    decimal: ",",
  },
  common: {
    cancel: "Скасувати",
    save: "Зберегти",
    saving: "Збереження…",
    close: "Закрити",
    delete: "Видалити",
    deleting: "Видалення…",
    rename: "Перейменувати",
    retry: "Повторити",
    refresh: "Оновити",
    open: "Відкрити",
    copy: "Копіювати",
    copied: "Скопійовано",
    loading: "Завантаження…",
    back: "Назад",
    search: "Пошук",
    all: "Усі",
    change: "Змінити",
    clear: "Очистити",
    send: "Надіслати",
    confirm: "Підтвердити",
    showMore: "Показати більше",
    showLess: "Згорнути",
    language: "Мова",
    theme: "Тема",
    signOut: "Вийти",
    privacy: "Конфіденційність",
    terms: "Умови використання",
    notFound: "Не знайдено",
    optional: "необов'язково",
    demoData: "Демо-дані",
    previous: "Попередній",
    next: "Наступний",
    expand: "Розгорнути",
    collapse: "Згорнути",
    externalLink: "Відкривається в новій вкладці",
    notifications: "Сповіщення",
    languages: {
      uk: "Українська",
      en: "English",
    },
    themes: {
      system: "Як у системі",
      light: "Світла",
      dark: "Темна",
    },
  },
  errors: {
    network: "Немає з'єднання із сервером. Перевірте мережу й спробуйте ще раз.",
    generic: "Не вдалося виконати запит.",
    codes: {
      no_model_access: "Доступ до моделі не налаштовано. Підключіть підписку ChatGPT або ключ OpenAI API у Налаштуваннях або попросіть доступ в адміністратора.",
      archive_unavailable: "Сесію не видалено: архів зараз недоступний. Спробуйте пізніше.",
    },
    loadFailed: "Не вдалося завантажити дані.",
    notFoundTitle: "Сторінку не знайдено",
    notFoundText: "Можливо, посилання застаріло або сторінку видалено.",
    sessionNotFound: "Сесію не знайдено",
    sessionNotFoundText: "Її могли видалити, або вона належить іншому обліковому запису.",
    toApp: "До класифікацій",
    tryAgain: "Спробувати ще раз",
    unexpected: "Щось пішло не так",
    unexpectedText: "Сторінка натрапила на помилку. Оновіть її, щоб продовжити.",
    reload: "Оновити сторінку",
  },
  auth: {
    title: "Вхід",
    welcome: "Вітаємо в Графі 33",
    subtitle: "Увійдіть, щоб класифікувати товари й зберігати історію сесій.",
    google: "Продовжити з Google",
    approvalNote: "Нові облікові записи підтверджує адміністратор.",
    devTitle: "Вхід для розробки",
    devHint: "Доступний лише на локальному сервері.",
    devEmail: "Email",
    devSubmit: "Увійти",
    agreePrefix: "Продовжуючи, ви погоджуєтеся з",
    agreeAnd: "та",
    termsLink: "умовами використання",
    privacyLink: "політикою конфіденційності",
    agreeSuffix: ".",
    copyright: "© 2026 Графа 33",
  },
  waitlist: {
    pendingTitle: "Ваш обліковий запис очікує на підтвердження",
    pendingText:
      "Адміністратор переглядає кожен новий обліковий запис. Щойно доступ відкриють, тут з'явиться робочий простір.",
    blockedTitle: "Обліковий запис заблоковано",
    blockedText: "Доступ до Графи 33 закрито. Зверніться до адміністратора, щоб його відновити.",
    signedInAs: "Ви увійшли як",
    refresh: "Перевірити ще раз",
    stillPending: "Обліковий запис ще не підтверджено.",
  },
  shell: {
    newClassification: "Нова класифікація",
    searchPlaceholder: "Пошук за назвою або кодом",
    searchLabel: "Пошук сесій",
    groups: {
      today: "Сьогодні",
      yesterday: "Учора",
      week: "Попередні 7 днів",
      older: "Раніше",
    },
    noSessions: "Ще немає класифікацій. Почніть з опису товару.",
    noMatches: "Нічого не знайдено",
    usage: "Використання",
    settings: "Налаштування",
    admin: "Адміністрування",
    adminBadge: (count: number) => `Чекають на вас: ${count}`,
    openMenu: "Відкрити меню",
    closeMenu: "Закрити меню",
    navigation: "Навігація",
    sessionMenu: "Дії із сесією",
    userMenu: "Меню користувача",
    untitled: "Нова сесія",
    renameTitle: "Перейменувати сесію",
    renameLabel: "Назва",
    deleteTitle: "Видалити сесію?",
    deleteText: (title: string) =>
      `Сесію «${title}» буде видалено разом із розмовою, джерелами та файлами. Її копія рік зберігається в архіві, а потім видаляється; на прохання адміністратор може стерти її раніше.`,
    deleted: "Сесію видалено",
    renamed: "Назву змінено",
    statusRunning: "Аналіз триває",
    statusClassified: "Класифіковано",
    statusClarifying: "Потрібне уточнення",
    statusRejected: "Не класифіковано",
    sessionsLabel: "Сесії",
    dictionary: "Довідник УКТЗЕД",
  },
  session: {
    statusNew: "Нова",
    statusRunning: "Аналізую…",
    statusClassified: "Класифіковано",
    statusClarifying: "Потрібне уточнення",
    statusRejected: "Не класифіковано",
    exportPdf: "Експорт PDF",
    exportUnavailable: "У демо-режимі PDF не формується.",
    panel: "Панель класифікації",
    showPanel: "Показати панель",
    hidePanel: "Сховати панель",
    renameHint: "Натисніть, щоб перейменувати",
    segments: {
      label: "Розділ сесії",
      chat: "Чат",
      classification: "Класифікація",
      sources: "Джерела",
    },
    readOnly: "Перегляд адміністратора — лише читання",
    openFile: "Відкрити файл",
    jumpToLatest: "До останнього",
    actions: "Дії",
    loadingSession: "Завантажую сесію…",
  },
  turn: {
    queued: "У черзі",
    running: "Аналізую…",
    doneIn: (duration: string) => `Готово за ${duration}`,
    failed: "Не вдалося завершити",
    interrupted: "Перервано",
    cancelled: "Зупинено",
    failedHint: "Аналіз зупинився з помилкою.",
    interruptedHint: "Сервер зупинився до завершення ходу. Повтор продовжить ту саму розмову.",
    cancelledHint: "Аналіз зупинено на ваш запит.",
    queuedHint: "Чекаю на вільного виконавця…",
    retry: "Повторити",
    retrying: "Повторюю…",
    retryStarted: "Хід поставлено в чергу знову",
    stop: "Зупинити",
    stopping: "Зупиняю…",
    stopRequested: "Зупиняю аналіз",
    process: "Хід роботи",
    stepsCount: (count: number) => `${n(count)} ${ukPlural(count, "крок", "кроки", "кроків")}`,
    noSteps: "Кроків ще немає",
    starting: "Починаю…",
    writing: "Пишу відповідь",
    elapsed: "Минуло",
    phaseNow: "Зараз",
    mark: "Графа 33",
    toggleStep: "Показати подробиці кроку",
    stepRunning: "Виконується",
    usage: {
      label: "Використання ходу",
      tokens: "Токени",
      inputOutput: "вхід / вихід",
      input: "Вхідні",
      cached: "З кешу",
      output: "Вихідні",
      reasoning: "Міркування",
      searches: "Вебпошуки",
      searchesCount: (count: number) => `${n(count)} ${ukPlural(count, "пошук", "пошуки", "пошуків")}`,
      apiEquivalent: "API-еквівалент",
      apiEquivalentEstimate: "API-еквівалент, оцінка",
      cacheWrite: "Запис у кеш",
      notReported: "не повідомляється",
      estimateWrites: "Бекенд ChatGPT не повідомляє записів у кеш, тож увесь некешований вхід пораховано як записаний.",
      estimateSearches: "Вебпошуки пораховано за запитами: OpenAI може рахувати пошук із кількома запитами як один виклик.",
      charged: "Списано",
      source: "Джерело",
      model: "Модель",
    },
    detail: {
      query: "Запит",
      url: "Адреса",
      tool: "Інструмент",
      arguments: "Аргументи",
      result: "Результат",
      error: "Помилка",
      codes: "Залишені коди",
      text: "Текст",
      started: "Початок",
      empty: "Подробиць немає.",
    },
    kinds: {
      reasoning: "Міркування",
      search: "Пошук",
      read: "Читання",
      tool: "Інструмент",
      tariff: "Тариф",
      note: "Примітка",
      warning: "Попередження",
    },
  },
  phases: {
    track: {
      research: "Дослідження",
      tariff: "Тариф",
      decisions: "Рішення",
    },
    raw: {
      research: "Дослідження товару",
      research_more: "Додатковий пошук",
      tariff: "Тарифний шлях",
      code_check: "Перевірка коду",
      fact: "Уточнення факту",
      evidence: "Пошук у збережених джерелах",
      lookup: "Пошук в інтернеті",
      enrichment: "Рішення та BTI",
      box31: "Графа 31",
      system: "Система",
    },
    pending: "Очікує",
    skipped: "Не знадобилось",
    missed: "Не виконано",
    inProgress: "Триває",
    trackLabel: "Етапи аналізу",
  },
  messages: {
    historyTitle: "Цей товар уже класифікували",
    historyText: "У вашій історії є схожі класифікації. Використайте код або запустіть новий аналіз.",
    reasonArticle: "Той самий артикул",
    reasonSimilar: "Схожий товар",
    declared: "задекларовано",
    chosen: "обраний",
    open: "Відкрити",
    useCode: "Використати цей код",
    newAnalysis: "Новий аналіз",
    resolvedReused: "Використано код із попередньої сесії.",
    resolvedNew: "Запущено новий аналіз.",
    classifiedOn: (date: string) => `класифіковано ${date}`,
    reuseCode: "Код",
    reusedFrom: "узято з сесії",
    reuseDone: "Код перенесено в цю сесію",
    analysisStarted: "Новий аналіз запущено",
    you: "Ви",
    noCode: "без коду",
  },
  access: {
    title: "Аналіз поки що недоступний",
    text: "Щоб аналізувати товари, підключіть свою підписку ChatGPT або ключ OpenAI API у Налаштуваннях або попросіть доступ в адміністратора.",
    sharedDown: "Підписка адміністратора, яку вам надали, зараз не підключена. Попросіть адміністратора або підключіть власний доступ у Налаштуваннях.",
    openSettings: "Відкрити Налаштування",
    ask: "Попросити адміністратора",
    asked: (date: string) => `Запит надіслано ${date}. Адміністратор побачить його в Адмініструванні.`,
    askAgain: "Нагадати",
    sent: "Запит надіслано адміністратору",
  },
  composer: {
    placeholder: "Назва, артикул, виробник, матеріал і призначення товару",
    placeholderFollowUp: "Відповідь на запитання або уточнення щодо товару",
    label: "Повідомлення",
    send: "Надіслати",
    attach: "Додати файл",
    attachHint: "PDF, зображення, DOCX, TXT, CSV · до 5 файлів по 25 МБ",
    busyHint: "Агент працює. Дочекайтеся відповіді або зупиніть аналіз.",
    queuedHint: "Хід у черзі. Дочекайтеся відповіді або зупиніть аналіз.",
    awaitingChoice: "Оберіть варіант вище, щоб продовжити.",
    readOnlyHint: "Перегляд адміністратора: надсилати повідомлення не можна.",
    tooMany: "Можна додати до 5 файлів.",
    tooLarge: (name: string) => `Файл «${name}» більший за 25 МБ.`,
    badType: (name: string) => `Формат файлу «${name}» не підтримується.`,
    uploadFailed: (name: string) => `Не вдалося завантажити «${name}».`,
    removeFile: (name: string) => `Прибрати файл «${name}»`,
    uploading: "Завантажується",
    shortcut: "Enter — надіслати · Shift+Enter — новий рядок",
    sendFailed: "Не вдалося надіслати повідомлення.",
    waitUploads: "Дочекайтеся завантаження файлів.",
  },
  empty: {
    title: "Що класифікуємо?",
    subtitle: "Опишіть один товар — агент знайде код УКТЗЕД, мито, рішення та BTI.",
    examplesLabel: "Приклади",
    examples: ["SKF 6205-2RS", "RINOL PU-S602", "Роликовий підшипник Volvo 11037841"],
    tipArticleTitle: "Додайте артикул і виробника",
    tipArticleText: "Точна модель допомагає знайти техлист і сторінку виробника.",
    tipAttachTitle: "Прикріпіть техлист або фото",
    tipAttachText: "PDF, фото чи специфікація уточнюють матеріал і призначення.",
  },
  panel: {
    tabs: {
      classification: "Класифікація",
      sources: "Джерела",
      images: "Зображення",
      files: "Файли",
    },
    empty: "Опишіть товар — тут з'являться коди УКТЗЕД, мито та рішення.",
    emptyRunning: "Агент аналізує товар. Коди з'являться, щойно він їх визначить.",
    good: "Товар",
    facts: {
      articleNumber: "Артикул",
      brand: "Торговельна марка",
      manufacturer: "Виробник",
      shape: "Форма",
      materials: "Матеріали",
      originCountry: "Походження",
      description: "Опис",
      other: "Інше",
    },
    factSources: (count: number) => `${n(count)} ${ukPlural(count, "джерело", "джерела", "джерел")}`,
    factNoSource: "Без цитати",
    factUnconfirmed: "не підтверджено",
    factSourcesTitle: (label: string) => `Джерела: ${label.toLowerCase()}`,
    factUnconfirmedHint: "Факт підтверджує лише одне джерело. Перевірте його перед декларуванням.",
    factNoQuoteHint: "Жодну цитату не знайдено в збережених джерелах. Перевірте факт перед декларуванням.",
    citationMessage: "Ваше повідомлення",
    citationOpen: "Відкрити джерело",
    quoted: (text: string) => `«${text}»`,
    pictureAlt: (index: number) => `Фото товару ${index}`,
    pictureOpen: "Відкрити фото",
    pictureSource: "Сторінка джерела",
    pictureNoSource: "Сторінка, на якій знайдено це фото, невідома.",
    pictureFile: "Файл зображення",
    pictureCounter: (index: number, total: number) => `${index} з ${total}`,
    allImages: (count: number) => `Усі зображення (${n(count)})`,
    stillNeeded: "Ще потрібно",
    stillNeededHint: "Відповідь у чаті допоможе обрати між можливими кодами.",
    chosenCode: "Обраний код",
    copyCode: "Копіювати код",
    codeCopied: "Код скопійовано",
    confidence: "Упевненість",
    duty: (duty: string) => `Мито: ${duty}`,
    reason: "Обґрунтування",
    ukrainianDecisions: "Українські рішення",
    europeanBti: "Європейські BTI",
    noUkrainianDecision: "Немає відповідного українського рішення.",
    noEuropeanBti: "Немає відповідного європейського BTI.",
    declaredCode: "Задекларований код",
    declaredHint: "Код, який ви вказали в графі 33 декларації.",
    useChosen: "Використати обраний код",
    anotherCode: "Інший код…",
    declaredSaved: "Задекларований код збережено",
    declaredCleared: "Задекларований код очищено",
    declaredMatches: "Збігається з обраним",
    declaredDiffers: "Відрізняється від обраного",
    declaredMark: "Задекларовано",
    useThisCode: "Використати цей код",
    useRuledOut: "Використати цей код…",
    ruledOutTitle: "Використати відкинутий код?",
    ruledOutText: (code: string) =>
      `Агент розглянув код ${code} і відкинув його з причини нижче. Вказуйте цей код у графі 33, лише якщо знаєте про товар те, що спростовує цю причину.`,
    feedbackTitle: "Код правильний?",
    feedbackYes: "Так, код правильний",
    feedbackNo: "Ні, код неправильний",
    feedbackDialogTitle: "Який код правильний?",
    feedbackDialogText: "Відгук переглядає адміністратор. На цю класифікацію він не впливає.",
    correctCode: "Правильний код",
    reasonLabel: "Чому",
    reasonPlaceholder: "Наприклад: зовнішній діаметр 52 мм, тому не підпозиція 8482 10 10",
    sendFeedback: "Надіслати відгук",
    feedbackThanks: "Дякуємо! Відгук передано на перевірку.",
    feedbackSentCorrect: "Ви позначили код як правильний",
    feedbackSentWrong: (code: string) => `Ви запропонували код ${code}`,
    feedbackAgain: "Змінити відгук",
    possibleCodes: "Можливі коди",
    possibleCodesHint: "Залишилися кілька кодів — уточнення в чаті визначить правильний.",
    comparedCodes: "Розглянуті коди",
    ruledOut: "Чому відкинуто",
    noChosen: "Жоден код поки не набрав достатньої впевненості.",
    rejected: "Агент не зміг класифікувати цей запит. Опишіть один конкретний товар.",
    codeRequired: "Оберіть 10-значний код із тарифу",
    tariffTitle: "Назва в тарифі",
  },
  combobox: {
    placeholder: "Код або слова з назви",
    hint: "Введіть щонайменше 2 символи",
    empty: "Нічого не знайдено",
    loading: "Шукаю в тарифі…",
    onlyTen: "Обрати можна лише 10-значні коди",
    pick: "Оберіть код",
    label: "Пошук у тарифі",
    error: "Не вдалося знайти в тарифі",
  },
  uktzed: {
    title: "Довідник УКТЗЕД",
    subtitle: "Розділи, групи, товарні позиції та примітки чинного тарифу.",
    titlesLanguage: "",
    levels: {
      section: "Розділ",
      chapter: "Група",
      heading: "Товарна позиція",
      subheading: "Товарна підпозиція",
      category: "Товарна категорія",
      subcategory: "Товарна підкатегорія",
    },
    levelsShort: {
      section: "розділ",
      chapter: "група",
      heading: "позиція",
      subheading: "підпозиція",
      category: "категорія",
      subcategory: "підкатегорія",
    },
    groupLine: "без коду",
    groupLineHint: "Назва без коду: об'єднує коди під нею",
    treeLabel: "Дерево УКТЗЕД",
    loadingLevel: "Завантажую рівень…",
    levelFailed: "Не вдалося завантажити цей рівень.",
    emptyLevel: "На цьому рівні немає кодів.",
    noTariffTitle: "Тариф ще не імпортовано",
    noTariffText: "Розділи та групи з'являться тут, щойно адміністратор завантажить тариф.",
    searchPlaceholder: "Код або слова з назви",
    searchLabel: "Пошук у довіднику УКТЗЕД",
    searchShortcut: "Натисніть /, щоб шукати",
    searchHint: "Введіть щонайменше 2 цифри коду або слово з 3 літер.",
    searching: "Шукаю…",
    searchEmpty: "Нічого не знайдено",
    searchEmptyHint: "Спробуйте інше слово або коротший код.",
    searchFailed: "Не вдалося виконати пошук.",
    searchMore: (count: number) => `Показано перші ${n(count)}. Уточніть запит, щоб побачити інші.`,
    nearest: "Найближчий рівень",
    nearestHint: "Такого коду немає в чинному тарифі. Ось найближчий рівень:",
    selectTitle: "Нічого не вибрано",
    selectText: "Розгорніть розділ у дереві або знайдіть код, щоб побачити назву, мито й примітки.",
    notFoundTitle: "Цього коду немає в чинному тарифі",
    notFoundText: (code: string) => `Код ${code} не знайдено. Можливо, його змінили або вилучили.`,
    searchFor: (code: string) => `Шукати ${code}`,
    breadcrumbs: "Шлях у тарифі",
    showInTree: "Показати в дереві",
    showDetails: "Деталі",
    dutyTitle: "Ставки ввізного мита",
    rates: {
      preferential: "Преференційна",
      reduced: "Пільгова",
      full: "Повна",
    },
    extraUnit: "Додаткова одиниця виміру",
    noExtraUnit: "немає",
    structure: "Структура коду",
    structureOf: (code: string) => `Структура коду ${code}`,
    fullTitle: "Повна назва",
    allLevels: (count: number) => `Усі рівні (${n(count)})`,
    fewerLevels: "Менше рівнів",
    openNode: (name: string) => `Відкрити в довіднику: ${name}`,
    notesTitle: "Примітки",
    noNotes: "До цього розділу та групи приміток немає.",
    noteGroup: {
      section: (numeral: string) => `Примітки до розділу ${numeral}`,
      chapter: (code: string) => `Примітки до групи ${code}`,
    },
    noteKinds: {
      notes: "Примітки",
      subheading: "Примітки до товарних підпозицій",
      additional: "Додаткові примітки",
      ukraine: "Додаткова примітка України",
    },
    noteNumber: (number: string) => `Примітка ${number}`,
    notesCount: (count: number) => `${n(count)} ${ukPlural(count, "примітка", "примітки", "приміток")}`,
    mentions: (code: string) => `Згадує ${code}`,
  },
  sources: {
    groups: {
      product: "Сторінки товару",
      decision: "Українські рішення",
      bti: "Європейські BTI",
      tariff: "Тариф",
      search: "Вебпошуки",
      image: "Зображення",
      upload: "Завантаження",
      other: "Інше",
    },
    empty: "Агент ще нічого не шукав і не відкривав.",
    savedCopy: "Збережена копія",
    failed: "Не вдалося",
    blocked: "Сайт заблокував доступ",
    query: "Запит",
    savedAt: (date: string) => `Збережено ${date}`,
    docError: "Не вдалося завантажити збережену копію.",
    openOriginal: "Відкрити оригінал",
    noTitle: "Без назви",
    noText: "Текст сторінки не збережено.",
    total: (count: number) => `${n(count)} ${ukPlural(count, "джерело", "джерела", "джерел")}`,
  },
  images: {
    empty: "Зображень ще немає. Тут з'являться фото товару, які знайде агент, і зображення, які ви прикріпите до повідомлення.",
    pictures: "Фото товару",
    uploads: "Ваші зображення",
    sources: "Зображення з джерел",
    uploadNote: "Ви прикріпили це зображення до повідомлення.",
    sourceLink: "Джерело",
  },
  files: {
    empty: "Документів ще немає. Прикріпіть техлист, специфікацію чи інвойс до повідомлення; PDF, які відкриє агент, теж з'являться тут.",
    uploads: "Ваші файли",
    stored: "Збережені копії джерел",
    open: "Відкрити",
    kinds: {
      pdf: "PDF",
      image: "Зображення",
      file: "Файл",
      page: "Сторінка",
    },
  },
  box31: {
    title: "Графа 31",
    titleFor: (code: string) => `Графа 31 · ${code}`,
    cardHint: "Опис товару для декларації",
    generate: "Згенерувати",
    open: "Відкрити",
    regenerate: "Згенерувати знову",
    versions: "3 варіанти",
    needsFacts: (count: number) => `Бракує даних: ${n(count)}`,
    outdated: "Застаріли",
    conflictBadge: "Не підтверджує код",
    failedBadge: "Не вдалося",
    queued: "У черзі",
    generating: "Пишу тексти",
    description:
      "Пункт 1: найменування та звичайний торговельний опис, за яким товар належить лише до цього коду. Тексти завжди українською.",
    progressQueued: "Генерацію поставлено в чергу…",
    progress: "Пишу тексти графи 31…",
    progressLookup: "Шукаю в джерелах факти, яких бракує…",
    steps: "Кроки генерації",
    progressKeep: "Попередні тексти залишаються, доки не з'являться нові.",
    empty: "Для цього коду текстів ще немає.",
    emptyHint: "Три варіанти — стислий, стандартний і розширений. Кожен достатній, щоб класифікувати товар.",
    failed: "Не вдалося згенерувати тексти",
    stopped: "Генерацію зупинено",
    tryAgain: "Спробувати ще раз",
    stale: "Факти сесії змінилися після генерації. Згенеруйте тексти знову.",
    conflictTitle: "Опис не підтверджує цей код",
    versionLabel: "Варіант тексту",
    kinds: {
      concise: "Стислий",
      standard: "Стандартний",
      detailed: "Розширений",
    },
    kindHints: {
      concise: "Мінімум для класифікації",
      standard: "Рекомендований: призначення, властивості, марка",
      detailed: "Захищає код від сусідніх",
    },
    length: (length: number, limit: number) => `${n(length)} з ${n(limit)} знаків`,
    copy: "Копіювати",
    copied: "Текст скопійовано",
    copyFailed: "Не вдалося скопіювати текст",
    copyWarnTitle: "У тексті є обов'язкові заповнювачі",
    copyWarnText: (tokens: string) =>
      `Замініть ${tokens} даними з документів перед поданням декларації. Скопіювати текст разом із заповнювачами?`,
    copyAnyway: "Скопіювати все одно",
    placeholders: "Заповнювачі",
    required: "Обов'язковий",
    recommended: "Рекомендований",
    what: "Що вписати",
    why: "Чому важливо",
    where: "Де знайти",
    warnings: "Зверніть увагу",
    criteria: "Чим код відрізняється від сусідніх",
    criterionStatus: {
      confirmed: "Підтверджено",
      missing: "Бракує факту",
      conflict: "Суперечить",
    },
    quotes: "Цитати з джерел",
    otherItems: "Пункти 2–4 заповнюєте ви",
    items: [
      "2 — кількість вантажних місць і код виду упаковки, якщо заповнено графу 6.",
      "3 — кількість і номери контейнерів або 0, якщо заповнено графу 19.",
      "4 — лише для періодичної МД, додаткової до неї декларації або тимчасового ввезення.",
    ],
    generatedAt: (date: string) => `Згенеровано ${date}`,
    ready: "Тексти графи 31 готові",
  },
  settings: {
    title: "Налаштування",
    subtitle: "Профіль і доступ до моделі для ваших класифікацій.",
    profile: "Профіль",
    profileText: "Ім'я та email беруться з облікового запису Google.",
    name: "Ім'я",
    email: "Email",
    language: "Мова інтерфейсу",
    languageHint: "Агент відповідає мовою, яку ви обрали.",
    languageSaved: "Мову змінено",
    modelAccess: "Доступ до моделі",
    modelAccessText: "Кожен хід агента виконується через одне з цих джерел.",
    effective: "Зараз використовується",
    nothingConnected: "Нічого не підключено",
    modes: {
      subscription: "Підписка ChatGPT",
      byok: "Власний ключ OpenAI",
      shared: "Підписка адміністратора",
      platform: "Ключ платформи",
      dev: "Розробницький вхід",
    },
    modesShort: {
      subscription: "Підписка",
      byok: "Свій ключ",
      shared: "Спільна",
      platform: "Платформа",
      dev: "Dev",
    },
    subscription: {
      text: "Ходи виконуються в межах вашого плану ChatGPT.",
      connect: "Підключити",
      connected: "Підключено",
      notConnected: "Не підключено",
      disconnect: "Відключити",
      disconnected: "Підписку відключено",
      error: "Помилка підключення",
      reconnect: "Підключити знову",
      continueLogin: "Продовжити підключення",
      since: (date: string) => `з ${date}`,
    },
    device: {
      title: "Підключення ChatGPT",
      description: "Увійдіть в OpenAI й підтвердьте код, щоб Графа 33 могла працювати з вашим планом.",
      step1: "Відкрийте сторінку входу OpenAI",
      openPage: "Відкрити сторінку OpenAI",
      step2: "Введіть цей код",
      copyCode: "Копіювати код",
      expiresIn: (time: string) => `Код дійсний ще ${time}`,
      expired: "Час дії коду минув. Почніть підключення знову.",
      warning:
        "OpenAI покаже попередження продовжувати, лише якщо вхід почато в Codex. Його почав Графа 33 на нашому сервері. Продовжуйте, лише якщо довіряєте Графа 33 доступ до вашого плану ChatGPT.",
      waiting: "Чекаю на підтвердження в OpenAI…",
      starting: "Готую вхід…",
      succeeded: "ChatGPT підключено",
      failed: "Не вдалося підключити ChatGPT",
      cancelled: "Підключення скасовано",
      cancel: "Скасувати вхід",
      tryAgain: "Почати знову",
    },
    byok: {
      text: "OpenAI виставляє рахунок за цей ключ напряму.",
      label: "Ключ OpenAI API",
      placeholder: "sk-…",
      save: "Зберегти ключ",
      saved: "Ключ збережено",
      remove: "Видалити ключ",
      removed: "Ключ видалено",
      savedLabel: "Збережений ключ",
      show: "Показати ключ",
      hide: "Сховати ключ",
    },
    shared: {
      enabled: (cap: string, spent: string) =>
        `Адміністратор надав вам свою підписку ChatGPT: ліміт ${cap} на місяць, використано ${spent} (оцінка, без списання)`,
      unavailable: "Підписка адміністратора зараз не підключена.",
    },
    platform: {
      enabled: (cap: string, spent: string) => `Увімкнено адміністратором: ліміт ${cap} на місяць, витрачено ${spent}`,
      enabledNoCap: (spent: string) => `Увімкнено адміністратором без ліміту, витрачено ${spent}`,
      disabled: "Не ввімкнено. Зверніться до адміністратора.",
      capUsed: "Використано ліміту",
    },
    dev: {
      text: "Локальний вхід Codex на цьому сервері. Лише для розробки.",
      available: "Доступний",
    },
    preferred: {
      title: "Бажане джерело",
      text: "Яке джерело використовувати для нових ходів.",
      automatic: "Автоматично",
      automaticText: "Підписка, потім власний ключ, потім підписка адміністратора, потім ключ платформи",
      fixedText: "Завжди це джерело",
      saved: "Бажане джерело збережено",
      unavailable: "не підключено",
    },
    connectedBadge: "Підключено",
    availableBadge: "Доступно",
    unavailableBadge: "Недоступно",
    offBadge: "Вимкнено",
    errorBadge: "Помилка",
  },
  usage: {
    title: "Використання",
    subtitle: "Ходи, токени й вартість за місяць.",
    month: "Місяць",
    prevMonth: "Попередній місяць",
    nextMonth: "Наступний місяць",
    tiles: {
      turns: "Ходи",
      inputTokens: "Вхідні токени",
      outputTokens: "Вихідні токени",
      webSearches: "Вебпошуки",
      cost: "API-еквівалент",
      charged: "Списано",
    },
    cachedShare: (share: string) => `${share} з кешу`,
    costHint: "Вартість ходів за цінами API",
    chargedHint: "Лише ходи через ключ платформи",
    daily: "За днями",
    metric: {
      label: "Показник",
      turns: "Ходи",
      cost: "Вартість",
      tokens: "Токени",
    },
    chartView: "Графік",
    tableView: "Таблиця",
    day: "День",
    bySource: "За джерелом",
    recent: "Останні ходи",
    columns: {
      session: "Сесія",
      status: "Статус",
      source: "Джерело",
      duration: "Тривалість",
      tokens: "Токени",
      cost: "Вартість",
      charge: "Списано",
      date: "Дата",
      turns: "Ходи",
    },
    empty: "За цей місяць ходів немає.",
    noSource: "—",
    chargeLabel: "списано",
    costLabel: "API",
    turnsCount: (count: number) => `${n(count)} ${ukPlural(count, "хід", "ходи", "ходів")}`,
  },
  turnStatus: {
    queued: "У черзі",
    running: "Виконується",
    succeeded: "Готово",
    failed: "Помилка",
    interrupted: "Перервано",
    cancelled: "Зупинено",
  },
  admin: {
    title: "Адміністрування",
    nav: {
      overview: "Огляд",
      tenants: "Клієнти",
      turns: "Ходи",
      feedback: "Відгуки",
      settings: "Налаштування",
    },
    overview: {
      subtitle: "Стан клієнтів, витрати за місяць і довідкові дані.",
      tenants: "Клієнти",
      pending: "Очікують підтвердження",
      active: "Активні",
      blocked: "Заблоковані",
      month: "Цей місяць",
      turns: "Ходи",
      cost: "API-еквівалент",
      charge: "Списано",
      failed: "Збої",
      avgDuration: "Середня тривалість",
      feedbackNew: "Нові відгуки",
      review: "Переглянути",
      data: "Довідкові дані",
      tariff: "Тариф УКТЗЕД",
      tariffInfo: (version: string, codes: string) => `Версія ${version} · ${codes} кодів`,
      ebti: "Європейські BTI",
      ebtiInfo: (rulings: number, valid: number) => `${n(rulings)} ${ukPlural(rulings, "рішення", "рішення", "рішень")}, з них чинних ${n(valid)}`,
      ebtiExport: (date: string) => `Вивантаження ЄС від ${date}`,
      ebtiLastCheck: (date: string, outcome: string) => `Остання перевірка ${date} — ${outcome}`,
      ebtiOutcome: {
        unchanged: "без змін",
        imported: "імпортовано",
        failed: "помилка",
      },
      ebtiNotChecked: "Ще не перевірялося",
      ebtiNextCheck: (date: string) => `Наступна перевірка ${date}`,
      ebtiLastImport: (date: string, duration: string, added: number, updated: number, removed: number) =>
        `Останній імпорт ${date} за ${duration}: нових ${n(added)}, змінених ${n(updated)}, вилучених ${n(removed)}`,
      updatedAt: (date: string) => `Оновлено ${date}`,
      never: "Ще не завантажено",
      refreshTariff: "Оновити тариф",
      checkEbti: "Перевірити зараз",
      jobStarted: "Завдання поставлено в чергу",
      jobs: "Останні завдання",
      noJobs: "Завдань ще не було.",
      jobType: {
        ebti_import: "Оновлення EBTI",
        tariff_fetch: "Оновлення тарифу",
        model_prices_refresh: "Оновлення цін моделей",
      },
      jobStatus: {
        queued: "У черзі",
        running: "Виконується",
        succeeded: "Готово",
        failed: "Помилка",
        cancelled: "Скасовано",
      },
      columns: {
        type: "Завдання",
        status: "Статус",
        created: "Створено",
        updated: "Оновлено",
        result: "Результат",
      },
    },
    tenants: {
      subtitle: "Підтвердження нових облікових записів, ваша підписка чи ключ платформи для них і ліміти.",
      search: "Пошук за email",
      statusFilter: "Статус",
      statuses: {
        pending: "Очікує",
        active: "Активний",
        blocked: "Заблокований",
      },
      columns: {
        tenant: "Клієнт",
        status: "Статус",
        created: "Створено",
        lastLogin: "Останній вхід",
        sources: "Джерела",
        access: "Доступ від вас",
        sharedSubscription: "Моя підписка",
        platformKey: "Ключ платформи",
        cap: "Ліміт, $/міс.",
        turns: "Ходи",
        cost: "API",
        charge: "Списано",
        actions: "Дії",
        activity: "Активність",
        month: "Цей місяць",
      },
      createdOn: (date: string) => `Створено ${date}`,
      lastLoginOn: (date: string) => `Вхід ${date}`,
      chargedShort: (value: string) => `${value} списано`,
      approve: "Підтвердити",
      block: "Заблокувати",
      unblock: "Розблокувати",
      noCap: "без ліміту",
      never: "ніколи",
      updated: "Клієнта оновлено",
      empty: "Клієнтів за цим фільтром немає.",
      platformKeyLabel: (email: string) => `Ключ платформи для ${email}`,
      sharedLabel: (email: string) => `Ваша підписка ChatGPT для ${email}`,
      sharedFrom: (email: string) => `Надає ${email}`,
      sharedOwn: "Ваш власний обліковий запис",
      sharedNeedsLogin: "Щоб надати підписку, підключіть її в Налаштуваннях.",
      sharedRisk:
        "Ходи вибраних користувачів виконуються на вашому плані ChatGPT і витрачають його ліміти. Умови OpenAI не дозволяють ділитися обліковим записом, тож його можуть заблокувати. Ліміт рахує оцінку вартості цих ходів; з них нічого не списується.",
      capLabel: (email: string) => `Місячний ліміт для ${email}`,
      accessRequested: (date: string) => `Просить доступ з ${date}`,
    },
    turns: {
      subtitle: "Усі ходи всіх клієнтів. Рядок відкриває трасування.",
      status: "Статус",
      tenant: "Клієнт",
      limit: "Кількість",
      anyStatus: "Будь-який статус",
      anyTenant: "Усі клієнти",
      columns: {
        tenant: "Клієнт",
        session: "Сесія",
        status: "Статус",
        source: "Джерело",
        model: "Модель",
        queuedAt: "У черзі з",
        duration: "Тривалість",
        tokens: "Токени",
        cost: "Вартість",
        error: "Помилка",
      },
      empty: "Ходів за цими фільтрами немає.",
      openTrace: "Відкрити трасування",
    },
    trace: {
      title: "Трасування ходу",
      back: "До ходів",
      userMessage: "Повідомлення користувача",
      steps: "Кроки",
      calls: "Виклики агента",
      noCalls: "Викликів агента немає.",
      phase: "Етап",
      model: "Модель",
      effort: "Зусилля",
      webSearch: "Вебпошук",
      thread: "Потік",
      resumed: "продовжено",
      newThread: "новий",
      timings: "Час",
      start: "Початок",
      firstItem: "Перший елемент",
      duration: "Тривалість",
      tokens: "Токени",
      input: "Вхідні",
      cached: "З кешу",
      cacheWrite: "Запис у кеш",
      output: "Вихідні",
      reasoning: "Міркування",
      searches: "Вебпошуки",
      cost: "Вартість",
      schema: "Схема відповіді",
      prompt: "Промпт",
      finalResponse: "Фінальна відповідь",
      items: "Елементи",
      noItems: "Елементів немає.",
      payload: "Вміст",
      tenant: "Клієнт",
      session: "Сесія",
      queuedAt: "У черзі з",
      source: "Джерело",
      error: "Помилка",
      showPrompt: "Показати промпт",
      hidePrompt: "Сховати промпт",
      showPayload: "Вміст",
      callTitle: (seq: number) => `Виклик ${seq}`,
      none: "—",
      estimate: "оцінка",
      longContextApplied: (count: number) =>
        `${n(count)} ${ukPlural(count, "запит моделі", "запити моделі", "запитів моделі")} понад поріг довгого контексту оплачено за тарифом довгого контексту.`,
      longContextUnknown: "Вхід перевищує поріг довгого контексту, але кількості токенів окремих запитів немає, тому застосовано базові тарифи. Вартість може бути заниженою.",
    },
    feedback: {
      subtitle: "Відгуки користувачів про коди. Нові — першими.",
      statuses: {
        new: "Нові",
        reviewed: "Переглянуті",
        dismissed: "Відхилені",
      },
      statusBadge: {
        new: "Новий",
        reviewed: "Переглянуто",
        dismissed: "Відхилено",
      },
      columns: {
        tenant: "Клієнт",
        user: "Користувач",
        session: "Сесія",
        verdict: "Вердикт",
        agentCode: "Код агента",
        proposedCode: "Запропонований код",
        reason: "Причина",
        date: "Дата",
        status: "Статус",
        actions: "Дії",
      },
      verdict: {
        correct: "Правильно",
        wrong: "Неправильно",
      },
      markReviewed: "Переглянуто",
      dismiss: "Відхилити",
      reopen: "Повернути в нові",
      noteTitle: (action: string) => `${action}: нотатка`,
      noteLabel: "Нотатка адміністратора",
      notePlaceholder: "Що зроблено або чому відхилено",
      saved: "Відгук оновлено",
      empty: "Відгуків за цим фільтром немає.",
      note: "Нотатка",
    },
    settings: {
      subtitle: "Модель класифікації, оплата ключа платформи та ціни моделей.",
      model: "Модель класифікації",
      modelText:
        "На цій моделі виконуються ходи всіх клієнтів. У списку — моделі OpenAI, які Codex може виконати для застосунку: текстова відповідь, виклик інструментів, структурована відповідь і міркування із зусиллям low і medium.",
      modelNotOffered: "Цієї моделі вже немає серед доступних. Ходи на ній і далі рахуються за її останніми цінами; оберіть іншу модель.",
      modelOption: (input: string, output: string, context: string) => `вхід ${input} · вихід ${output} $ за 1 млн · контекст ${context}`,
      billing: "Оплата",
      billingText: "Хід на ключі платформи списується за API-вартістю з націнкою. У models.dev немає ціни вебпошуку, тому її задають тут.",
      webSearchPrice: "Ціна вебпошуку, $ за виклик",
      markup: "Націнка на ключ платформи, %",
      defaultCap: "Ліміт за замовчуванням, $ на місяць",
      prices: "Ціни моделей",
      pricesText: "Вартість ходів рахується за цією таблицею, $ за 1 млн токенів. Її щодня завантажують із models.dev; редагувати її не можна.",
      pricesLegend:
        "Другий рядок — тариф для запиту, вхід якого перевищує поріг довгого контексту; він діє на весь запит. Риска — окремої ціни немає: кешований вхід оплачується як звичайний, а запис у кеш окремо не оплачується.",
      updatedFrom: (date: string, source: string) => `Оновлено ${date} з ${source}`,
      neverFetched: (source: string) => `Ще не завантажено з ${source}`,
      refreshNow: "Оновити зараз",
      refreshing: "Оновлюю…",
      refreshQueued: "Оновлення цін поставлено в чергу",
      refreshDone: "Ціни моделей оновлено",
      refreshFailed: "Не вдалося оновити ціни моделей",
      lastError: (date: string) => `Останнє оновлення ${date} не вдалося`,
      keptPrices: "У таблиці залишено ціни з останнього вдалого оновлення.",
      priceColumns: {
        model: "Модель",
        context: "Контекст",
        input: "Вхід",
        cached: "Вхід з кешу",
        cacheWrite: "Запис у кеш",
        output: "Вихід",
        longContext: "Довгий контекст",
      },
      longContextAbove: (tokens: string) => `понад ${tokens}`,
      status: {
        active: "доступна",
        deprecated: "застаріла",
        removed: "вилучена з models.dev",
      },
      inUse: "у роботі",
      facts: {
        context: "Контекст",
        output: "Найдовша відповідь",
        efforts: "Зусилля міркування",
        released: "Випуск",
        knowledge: "Знання до",
        fetched: "Завантажено",
        prices: "Ціни, $ за 1 млн токенів",
        longContext: (tokens: string) => `Запит понад ${tokens} токенів`,
      },
      saved: "Налаштування збережено",
      reset: "Скинути зміни",
      unsaved: "Є незбережені зміни",
      invalid: "Перевірте значення: числа мають бути невід'ємними.",
    },
    session: {
      title: "Сесія клієнта",
      back: "Назад",
    },
  },
  legal: {
    draft: "Чернетка",
    updated: "Редакція від 3 листопада 2026 року",
    backToSignIn: "До входу",
    backToApp: "До застосунку",
    privacy: {
      title: "Політика конфіденційності",
      intro:
        "Ця чернетка пояснює, які дані обробляє Графа 33, навіщо і як довго їх зберігає. Остаточну редакцію буде опубліковано до запуску сервісу.",
      sections: [
        {
          title: "Які дані ми збираємо",
          body: "Ім'я, email і фото профілю з облікового запису Google; описи товарів, повідомлення й файли, які ви додаєте до сесій; сторінки й документи, які агент відкриває під час аналізу; технічні журнали та облік використання моделі.",
        },
        {
          title: "Навіщо ми їх використовуємо",
          body: "Щоб класифікувати товари, показувати історію сесій, рахувати використання та запобігати зловживанням. Ми не продаємо дані й не використовуємо ваші сесії для реклами.",
        },
        {
          title: "Хто ще отримує дані",
          body: "Опис товару й вкладення передаються постачальнику моделі (OpenAI) через джерело, яке ви обрали: вашу підписку ChatGPT, власний ключ або ключ платформи. Загальнодоступні сторінки, які прочитав агент, можуть зберігатися в спільному кеші без прив'язки до вас.",
        },
        {
          title: "Зберігання та видалення",
          body: "Сесії зберігаються, доки ви їх не видалите. Видалена сесія разом із повідомленнями, джерелами та файлами рік зберігається в архіві в ЄС, а потім видаляється. Щоб видалити обліковий запис повністю або стерти свої дані й з архіву, напишіть адміністратору.",
        },
        {
          title: "Ваші права",
          body: "Ви можете отримати копію своїх даних, виправити їх або вимагати видалення. Звертайтеся до адміністратора сервісу.",
        },
      ],
    },
    terms: {
      title: "Умови використання",
      intro:
        "Ця чернетка описує правила користування Графою 33. Остаточну редакцію буде опубліковано до запуску сервісу.",
      sections: [
        {
          title: "Що робить сервіс",
          body: "Графа 33 допомагає визначити код УКТЗЕД товару: досліджує товар, проходить тарифом і показує рішення та BTI. Результат — це підказка для фахівця, а не попереднє рішення митного органу.",
        },
        {
          title: "Ваша відповідальність",
          body: "Код у митній декларації обираєте ви. Перевіряйте обґрунтування й джерела перед декларуванням. Не завантажуйте дані, на які не маєте права.",
        },
        {
          title: "Облікові записи",
          body: "Новий обліковий запис підтверджує адміністратор. Ми можемо заблокувати доступ у разі зловживань або несплати використання ключа платформи.",
        },
        {
          title: "Оплата",
          body: "Ходи через вашу підписку ChatGPT або власний ключ оплачуються напряму OpenAI. Ходи через ключ платформи обліковуються за опублікованими цінами з націнкою.",
        },
        {
          title: "Обмеження відповідальності",
          body: "Сервіс надається «як є». Ми не відповідаємо за рішення, ухвалені на основі результатів класифікації, і за наслідки неправильного декларування.",
        },
      ],
    },
  },
};

export type Dict = typeof uk;
